import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useGroupStore } from '@/stores/groupStore';
import { useAuthStore } from '@/stores/authStore';
import { GroupMemberTile } from '@/components/GroupMemberTile';

export function GroupMembersPage() {
  const { groupId } = useParams<{ groupId: string }>();
  const { user } = useAuthStore();
  const {
    currentGroup,
    members,
    isLoadingMembers,
    isLoadingMoreMembers,
    membersHasMore,
    loadGroupDetail,
    loadMembers,
    loadMoreMembers,
    approveMember,
    rejectMember,
    removeMember,
    banMember,
    unbanMember,
    muteMember,
    unmuteMember,
    updateMemberRole,
    transferOwnership,
  } = useGroupStore();

  useEffect(() => {
    if (groupId) {
      if (!currentGroup || currentGroup.id !== groupId) {
        loadGroupDetail(groupId);
      }
      loadMembers(groupId);
    }
  }, [groupId]);

  const canManage = currentGroup?.myRole === 'owner' || currentGroup?.myRole === 'moderator';
  const isOwner = currentGroup?.myRole === 'owner';

  const pendingMembers = members.filter(
    (m) => m.status === 'pending_invite' || m.status === 'requested'
  );
  const activeMembers = members.filter(
    (m) => m.status === 'active' || m.status === 'muted'
  );
  const bannedMembers = members.filter((m) => m.status === 'banned');

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 200 && membersHasMore && !isLoadingMoreMembers && groupId) {
      loadMoreMembers(groupId);
    }
  };

  return (
    <div className="h-[calc(100vh-7.5rem)] overflow-y-auto" onScroll={handleScroll}>
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background border-b border-border px-4 py-3">
        <div className="flex items-center gap-3">
          <Link to={groupId ? `/groups/${groupId}` : '/groups'} className="text-muted-foreground hover:text-primary transition-colors">
            &larr;
          </Link>
          <h1 className="text-lg font-bold text-card-foreground">Members</h1>
          {currentGroup && (
            <span className="text-sm text-muted-foreground">
              {currentGroup.memberCount} total
            </span>
          )}
        </div>
      </div>

      {/* Loading */}
      {isLoadingMembers && members.length === 0 && (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      )}

      {/* Pending section */}
      {canManage && pendingMembers.length > 0 && (
        <div className="border-b border-border">
          <div className="px-4 py-2 bg-yellow-500/5">
            <h3 className="text-xs font-medium text-yellow-600 uppercase tracking-wider">
              Pending ({pendingMembers.length})
            </h3>
          </div>
          {pendingMembers.map((member) => (
            <GroupMemberTile
              key={member.id}
              member={member}
              canManage={canManage}
              isOwner={isOwner}
              currentUserId={user?.id}
              onApprove={(uid) => groupId && approveMember(groupId, uid)}
              onReject={(uid) => groupId && rejectMember(groupId, uid)}
            />
          ))}
        </div>
      )}

      {/* Active members */}
      {activeMembers.length > 0 && (
        <div>
          <div className="px-4 py-2">
            <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Members ({activeMembers.length})
            </h3>
          </div>
          {activeMembers.map((member) => (
            <GroupMemberTile
              key={member.id}
              member={member}
              canManage={canManage}
              isOwner={isOwner}
              currentUserId={user?.id}
              onRemove={(uid) => groupId && removeMember(groupId, uid)}
              onBan={(uid) => groupId && banMember(groupId, uid)}
              onUnban={(uid) => groupId && unbanMember(groupId, uid)}
              onMute={(uid) => groupId && muteMember(groupId, uid)}
              onUnmute={(uid) => groupId && unmuteMember(groupId, uid)}
              onPromote={(uid) => groupId && updateMemberRole(groupId, uid, 'moderator')}
              onDemote={(uid) => groupId && updateMemberRole(groupId, uid, 'member')}
              onTransfer={(uid) => groupId && transferOwnership(groupId, uid)}
            />
          ))}
        </div>
      )}

      {/* Banned members */}
      {canManage && bannedMembers.length > 0 && (
        <div className="border-t border-border">
          <div className="px-4 py-2 bg-red-500/5">
            <h3 className="text-xs font-medium text-red-500 uppercase tracking-wider">
              Banned ({bannedMembers.length})
            </h3>
          </div>
          {bannedMembers.map((member) => (
            <GroupMemberTile
              key={member.id}
              member={member}
              canManage={canManage}
              isOwner={isOwner}
              currentUserId={user?.id}
              onUnban={(uid) => groupId && unbanMember(groupId, uid)}
            />
          ))}
        </div>
      )}

      {/* Loading more */}
      {isLoadingMoreMembers && (
        <div className="flex justify-center py-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
        </div>
      )}
    </div>
  );
}
